import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import '../custom.css';

const Platform = () => {
    const toast = useRef(null);
    const inputRef = useRef(null);

    const [platformName, setPlatformName] = useState('');
    const [platforms, setPlatforms] = useState([]);
    const [editingPlatform, setEditingPlatform] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        fetchPlatforms();
    }, []);

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms(res.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch platforms' });
        }
    };

    const handleSave = async () => {
        try {
            let res;
            if (editingPlatform) {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/platform', { id: editingPlatform.platform_id, name: platformName });
            } else {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/platform', { name: platformName });
            }

            if (res.data.status === 'success') {
                toast.current.show({
                    severity: 'success',
                    summary: editingPlatform ? 'Updated' : 'Added',
                    detail: editingPlatform ? 'Platform updated successfully' : 'Platform added successfully'
                });
                setPlatformName('');
                setEditingPlatform(null);
                fetchPlatforms();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.data.message || 'Failed to save platform'
                });
            }
        } catch (err) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to save platform'
            });
        }
    };

    const confirmSave = () => {
        if (!platformName.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Platform name is required' });
            return;
        }

        confirmDialog({
            message: editingPlatform ? 'Are you sure you want to update this platform?' : 'Are you sure you want to add this platform?',
            header: editingPlatform ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-secondary',
            accept: () => handleSave()
        });
    };

    const handleEdit = (rowData) => {
        setEditingPlatform(rowData);
        setPlatformName(rowData.platform_name);

        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    const confirmCancel = () => {
        confirmDialog({
            message: 'Are you sure you want to cancel editing?',
            header: 'Confirm Cancel',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-secondary',
            accept: () => {
                setEditingPlatform(null);
                setPlatformName('');
            }
        });
    };

    const actionBodyTemplate = (rowData) => {
        return <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => handleEdit(rowData)} />;
    };

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Platform</h2>

            <div className="card">
                {/* Add / Edit Form */}
                <div className="flex flex-wrap align-items-end gap-3 mb-4">
                    <InputText ref={inputRef} value={platformName} onChange={(e) => setPlatformName(e.target.value)} placeholder="Enter Platform" style={{ width: '250px' }} />
                    <Button label={editingPlatform ? 'Update' : 'Save'} icon="pi pi-save" className="p-button-success" onClick={confirmSave} />
                    {editingPlatform && <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={confirmCancel} />}
                </div>
            </div>

            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={platforms}
                    showGridlines
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm"
                    globalFilter={globalFilter}
                >
                    <Column field="platform_id" header="ID" />
                    <Column field="platform_name" header="Platform Name" />
                    <Column header="Edit" body={actionBodyTemplate} />
                </DataTable>
            </div>
        </>
    );
};

export default Platform;
